<template>
  <v-card :loading="accountStore.isPlanLoading" class="plan-details-card">
    <v-card-item class="pb-1">
      <template v-slot:prepend>
        <v-avatar color="primary" variant="tonal" size="40">
          <v-icon icon="mdi-file-document-outline" size="22" />
        </v-avatar>
      </template>
      <v-card-title>
        {{ $t("page.account.planDetails") }}
      </v-card-title>
    </v-card-item>

    <v-divider />

    <template v-if="!accountStore.isPlanLoading">
      <v-card-text class="pt-3 pb-2">
        <div class="plan-details-layout">
          <div class="plan-meta-panel">
            <div class="plan-meta-item">
              <div class="app-field-label detail-label">{{ $t("page.account.plan.plan") }}</div>
              <div class="detail-value text-truncate">{{ planName }}</div>
            </div>

            <v-divider vertical class="plan-meta-divider" />

            <div class="plan-meta-item">
              <div class="app-field-label detail-label">
                {{ $t("page.account.plan.billingCycle") }}
              </div>
              <div v-if="props.subscription.cancelAtPeriodEnd === true" class="detail-value">
                {{ $t("common.action.cancelsOn") }}
                <span v-date="props.subscription.periodEnd" />
              </div>
              <div v-else class="detail-value">{{ billingCycle }}</div>
            </div>
          </div>

          <div class="usage-panel">
            <div class="d-flex align-center justify-space-between ga-3 mb-2">
              <div class="app-field-label">
                {{ $t("page.account.plan.planUsage") }}
              </div>
              <v-chip color="secondary" variant="tonal" size="small">
                {{ usage?.activeTerminal || 0 }}/{{ usage?.subscriptionQuantity || 1 }}
                {{ $t("page.account.plan.terminals") }}
              </v-chip>
            </div>
            <v-progress-linear
              class="usage-progress"
              rounded
              rounded-bar
              height="10"
              color="primary"
              :model-value="usageNum"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="px-4 pb-3 pt-0 justify-end">
        <v-btn
          v-if="props.plan.name"
          color="primary"
          variant="flat"
          class="px-6"
          :loading="accountStore.subscriptionUpdateLinkLoading"
          @click="updateTerminals"
        >
          {{ $t("page.account.plan.updateTerminals") }}
        </v-btn>
        <v-btn v-else color="primary" :loading="loading" @click="upgradePlan">
          {{ $t("page.account.plan.upgrade") }}
        </v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script lang="ts" setup>
import { useAccountStore } from "@/account/stores/account";
import i18n from "@/app/plugins/i18n";
import { computed, onMounted, ref } from "vue";

const props = defineProps({
  plan: {
    type: Object,
    default: () => ({}),
  },
  subscription: {
    type: Object,
    default: () => ({}),
  },
});

const accountStore = useAccountStore();
const usage = ref<{ activeTerminal?: string; subscriptionQuantity?: string }>();
const usageNum = ref(0);
const customerPortalLink = ref<{ url?: string } | undefined>(undefined);
const loading = ref(false);

const planName = computed(() => props.plan.name || i18n.global.t("page.account.plan.starter"));

const billingCycle = computed(() => {
  if (!props.plan.billingCycle) return "-";

  const translationKey = `page.account.plan.${props.plan.billingCycle}`;
  return i18n.global.te(translationKey) ? i18n.global.t(translationKey) : props.plan.billingCycle;
});

onMounted(() => {
  accountStore
    .getAccountUsage()
    .then((response) => {
      usage.value = { ...response };
      usageNum.value =
        (parseFloat(usage.value?.activeTerminal || "0") /
          parseFloat(usage.value?.subscriptionQuantity || "1")) *
        100;
    })
    .catch(() => {
      usage.value = { activeTerminal: "0", subscriptionQuantity: "1" };
      usageNum.value = 0;
    });
});

const upgradePlan = async () => {
  loading.value = true;
  customerPortalLink.value = await accountStore.createCheckoutSessionLink();
  if (customerPortalLink.value?.url) window.open(customerPortalLink.value.url, "_blank");
  loading.value = false;
};

const updateTerminals = async () => {
  if (accountStore.subscription?.id) {
    const customerPortal = await accountStore.createPortalUpdateLink();
    if (customerPortal?.url) window.open(customerPortal.url, "_blank");
  }
};
</script>

<style scoped>
.plan-details-card {
  width: 100%;
}

.plan-details-layout {
  display: grid;
  grid-template-columns: minmax(18rem, 1fr) minmax(18rem, 1fr);
  gap: 12px;
  align-items: stretch;
}

.plan-meta-panel,
.usage-panel {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  border-radius: 12px;
  padding: 12px;
}

.plan-meta-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.detail-label {
  margin-bottom: 6px;
}

.plan-name {
  color: rgba(var(--v-theme-on-surface), 0.9);
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.2;
}

.detail-value {
  color: rgba(var(--v-theme-on-surface), 0.86);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.25;
}

.usage-progress {
  max-width: 22rem;
}

@media (max-width: 1279px) {
  .plan-details-layout {
    grid-template-columns: 1fr;
  }

  .usage-progress {
    max-width: 22rem;
  }
}

@media (max-width: 599px) {
  .plan-meta-panel {
    grid-template-columns: 1fr;
  }

  .plan-meta-divider {
    display: none;
  }
}
</style>
